<script setup lang="ts">
import { useIoStore } from '@/store';
import { usePlannerStore } from '@/store/integrationStore/plannerStore';
import { useTodoStore } from '@/store/integrationStore/todoStore';
import { useMapStore } from '@/store/remapStore';
import { getMSIntegrationConfig } from '@/utils/helpers/msIntegrationHelpers';

const emit = defineEmits<{
  (e: 'close'): void;
}>();
const messages = useAlerts();
const plannerStore = usePlannerStore();
const todoStore = useTodoStore();
const ioStore = useIoStore();
const mapStore = useMapStore();
const isLoggedIn = ref(false);

// Get MS integration configuration for current tenant
const msIntegrationConfig = getMSIntegrationConfig();

// Export destination selection - no default until logged in
const exportDestination = ref<'planner' | 'todo' | null>(null);

// Track which destination the user is authenticated for
const authenticatedFor = ref<'planner' | 'todo' | 'both' | null>(null);

// Export mode selection - no default until logged in
const exportMode = ref<'phase' | 'selected' | null>(null);

// Check if activities are selected
const hasSelectedActivities = computed(() => {
  return (
    mapStore.selectedContext === 'activity' &&
    (mapStore.selectedIds.length > 0 || !!mapStore.selectedId)
  );
});

// Get selected activity IDs
const selectedActivityIds = computed(() => {
  if (mapStore.selectedIds.length > 0) {
    return mapStore.selectedIds;
  } else if (mapStore.selectedId) {
    return [mapStore.selectedId];
  }

  return [];
});

// No auto-selection on mount - handled in login function

const groupSelected = computed(() => {
  return selectedGroup.value !== null && selectedGroup.value !== '';
});
const plannerSelected = computed(() => {
  return selectedPlanner.value !== null && selectedPlanner.value !== '';
});
const todoListSelected = computed(() => {
  return selectedTodoList.value !== null && selectedTodoList.value !== '';
});
const phaseSelected = computed(() => {
  return selectedPhase.value !== null && selectedPhase.value !== '';
});
const canExport = computed(() => {
  // Must have both destination and mode selected
  if (!exportDestination.value || !exportMode.value) {
    return false;
  }

  const hasContent =
    exportMode.value === 'phase'
      ? phaseSelected.value
      : hasSelectedActivities.value;

  if (exportDestination.value === 'planner') {
    return hasContent && groupSelected.value && plannerSelected.value;
  } else {
    // For Todo, also check if there are any lists available
    return (
      hasContent && todoListSelected.value && todoStore.todoLists.length > 0
    );
  }
});

function cancel() {
  if (!plannerStore.isExporting) {
    plannerStore.isExporting = false;
  }
  emit('close');
}
const selectedGroup = ref<string | null>(null);
const selectedPlanner = ref<string | null>(null);
const selectedTodoList = ref<string | null>(null);
const selectedPhase = ref<string | null>(null);
const clickedExport = ref<boolean>(false);
const isLoading = ref(false);
const includeCriteriaAsChecklist = ref(false);
const includeCriteriaAsChecklistTodo = ref(false);

// Check for existing authentication on mount
onMounted(async () => {
  isLoading.value = true;
  try {
    let isPlannerAuthenticated = false;
    let isTodoAuthenticated = false;

    // Check authentication for each enabled service
    if (msIntegrationConfig.isPlannerEnabled) {
      isPlannerAuthenticated = await plannerStore.checkAuthentication();
    }
    if (msIntegrationConfig.isTodoEnabled) {
      isTodoAuthenticated = await todoStore.checkAuthentication();
    }

    const isAuthenticated = isPlannerAuthenticated || isTodoAuthenticated;

    if (isAuthenticated) {
      // User is already authenticated, mark as logged in
      isLoggedIn.value = true;

      // Set default destination based on what's enabled and authenticated
      if (!exportDestination.value) {
        if (isPlannerAuthenticated && msIntegrationConfig.isPlannerEnabled) {
          exportDestination.value = 'planner';
        } else if (isTodoAuthenticated && msIntegrationConfig.isTodoEnabled) {
          exportDestination.value = 'todo';
        }
      }

      if (!exportMode.value && hasSelectedActivities.value) {
        exportMode.value = 'selected';
      } else if (!exportMode.value) {
        exportMode.value = 'phase';
      }

      // Track which services are authenticated
      if (isPlannerAuthenticated && isTodoAuthenticated) {
        authenticatedFor.value = 'both';
      } else if (isPlannerAuthenticated) {
        authenticatedFor.value = 'planner';
      } else if (isTodoAuthenticated) {
        authenticatedFor.value = 'todo';
      }

      // Load necessary data based on authentication
      if (isPlannerAuthenticated) {
        await plannerStore.loadUserInfos();
        await plannerStore.loadAllPlans();
      }
      if (isTodoAuthenticated) {
        await todoStore.loadUserInfo();
        await todoStore.loadTodoLists();
      }
    }
  } catch (error) {
    console.error('Error during authentication check:', error);
  } finally {
    isLoading.value = false;
  }
});

const groups = computed(() => {
  return plannerStore.groups
    .map((group) => ({
      label: group.displayName,
      value: group.id,
    }))
    .sort((a, b) => (a.label.toLowerCase() < b.label.toLowerCase() ? -1 : 1));
});

const planners = computed(() => {
  if (!selectedGroup.value) {
    return [];
  }

  return plannerStore
    .plannersForGroup(selectedGroup.value)
    .map((planner) => ({
      label: planner.title,
      value: planner.id,
    }))
    .sort((a, b) => (a.label.toLowerCase() < b.label.toLowerCase() ? -1 : 1));
});

const todoLists = computed(() => {
  // If no lists exist, show a placeholder
  if (!todoStore.todoLists || todoStore.todoLists.length === 0) {
    return [];
  }

  return todoStore.todoLists
    .filter((list) => list.isOwner !== false) // Include lists where isOwner is true or undefined
    .map((list) => ({
      label: list.displayName,
      value: list.id,
    }))
    .sort((a, b) => (a.label.toLowerCase() < b.label.toLowerCase() ? -1 : 1));
});

const phases = computed(() => {
  return ioStore.phases.map((stream) => ({
    label: stream.title,
    value: stream.id,
  }));
});

async function login() {
  try {
    isLoading.value = true;

    // Login to both enabled services
    if (msIntegrationConfig.isPlannerEnabled) {
      await plannerStore.login();
      await plannerStore.loadUserInfos();
      await plannerStore.loadAllPlans();
    }

    if (msIntegrationConfig.isTodoEnabled) {
      await todoStore.login();
      await todoStore.loadUserInfo();
      await todoStore.loadTodoLists();

      // If no lists exist, show a warning
      if (!todoStore.todoLists || todoStore.todoLists.length === 0) {
        messages.warning(
          'No Todo lists found. Please create a list in Microsoft Todo first.',
        );
      }
    }

    // Set default destination based on what's enabled
    if (!exportDestination.value) {
      if (msIntegrationConfig.isPlannerEnabled) {
        exportDestination.value = 'planner';
      } else if (msIntegrationConfig.isTodoEnabled) {
        exportDestination.value = 'todo';
      }
    }

    // Set export mode based on whether activities are selected
    if (!exportMode.value) {
      exportMode.value = hasSelectedActivities.value ? 'selected' : 'phase';
    }

    isLoggedIn.value = true;

    // Track which services are authenticated
    if (msIntegrationConfig.hasBothEnabled) {
      authenticatedFor.value = 'both';
    } else if (msIntegrationConfig.isPlannerEnabled) {
      authenticatedFor.value = 'planner';
    } else if (msIntegrationConfig.isTodoEnabled) {
      authenticatedFor.value = 'todo';
    }
  } catch (error) {
    console.error('Login failed:', error);
    messages.error('Login failed. Please try again.');
    isLoggedIn.value = false;
    authenticatedFor.value = null;
  } finally {
    isLoading.value = false;
  }
}

async function handleExport() {
  clickedExport.value = true;
  isLoading.value = true;

  try {
    if (exportDestination.value === 'planner') {
      // Export to Planner
      if (selectedPlanner.value !== null && selectedPlanner.value !== '') {
        // Get the group ID from the selected planner
        const groupId = plannerStore.getGroupIdForPlan(selectedPlanner.value);
        if (!groupId) {
          throw new Error('Could not find group for selected planner');
        }

        if (exportMode.value === 'phase') {
          if (selectedPhase.value !== null && selectedPhase.value !== '') {
            await plannerStore.export(
              groupId,
              selectedPhase.value,
              selectedPlanner.value,
              includeCriteriaAsChecklist.value,
            );
          }
        } else {
          // Export selected activities
          await plannerStore.exportSelectedActivities(
            groupId,
            selectedActivityIds.value,
            selectedPlanner.value,
            includeCriteriaAsChecklist.value,
          );
        }
      }
    } else {
      // Export to Todo
      if (selectedTodoList.value !== null && selectedTodoList.value !== '') {
        if (exportMode.value === 'phase') {
          if (selectedPhase.value !== null && selectedPhase.value !== '') {
            await todoStore.exportPhaseToTodo(
              selectedTodoList.value,
              selectedPhase.value,
              includeCriteriaAsChecklistTodo.value,
            );
          }
        } else {
          // Export selected activities
          await todoStore.exportSelectedActivitiesToTodo(
            selectedTodoList.value,
            selectedActivityIds.value,
            includeCriteriaAsChecklistTodo.value,
          );
        }
      }
    }

    messages.success(
      exportDestination.value === 'planner'
        ? $t('msPlannerIntegration.successMessage')
        : $t('msTodoIntegration.successMessage'),
    );
  } catch {
    messages.error(
      exportDestination.value === 'planner'
        ? $t('msPlannerIntegration.errorMessage')
        : $t('msTodoIntegration.errorMessage'),
    );
  } finally {
    isLoading.value = false;
    emit('close');
  }
}

// Clear planner selection when group changes
watch(selectedGroup, () => {
  selectedPlanner.value = null;
});

// Watch for destination changes to reload appropriate data
watch(exportDestination, async (newDestination) => {
  // Only proceed if user is logged in
  if (isLoggedIn.value) {
    // Clear previous selections when switching
    selectedGroup.value = null;
    selectedPlanner.value = null;
    selectedTodoList.value = null;
    selectedPhase.value = null;

    try {
      if (newDestination === 'planner') {
        // Load Planner data if not already loaded
        if (plannerStore.allPlans.length === 0) {
          await plannerStore.loadUserInfos();
          await plannerStore.loadAllPlans();
        }
      } else if (newDestination === 'todo') {
        // Load Todo data if not already loaded
        if (todoStore.todoLists.length === 0) {
          await todoStore.loadUserInfo();
          await todoStore.loadTodoLists();

          // If no lists exist, show a warning
          if (todoStore.todoLists.length === 0) {
            messages.warning(
              'No Todo lists found. Please create a list in Microsoft Todo first.',
            );
          }
        }
      }
    } catch (error) {
      console.error('Failed to load data for ' + newDestination + ':', error);
      messages.error('Failed to load data. Please try again.');
    }
  }
});
</script>

<template>
  <UiModal :title="$t('msPlannerIntegration.title')" @close="cancel">
    <div class="main">
      <UiButton v-if="!isLoggedIn" @click="login">
        {{ $t('msPlannerIntegration.login') }}
      </UiButton>

      <!-- Export Destination Selection - only show if both are enabled -->
      <div
        v-if="isLoggedIn && msIntegrationConfig.hasBothEnabled"
        class="export-destination-selection"
      >
        <label class="export-destination-label">
          {{ $t('msIntegration.exportDestination') }}:
        </label>
        <div class="export-destination-options">
          <label
            v-if="msIntegrationConfig.isPlannerEnabled"
            class="radio-option"
          >
            <input
              v-model="exportDestination"
              type="radio"
              value="planner"
              name="exportDestination"
            />
            <span>{{ $t('msIntegration.exportToPlanner') }}</span>
          </label>
          <label v-if="msIntegrationConfig.isTodoEnabled" class="radio-option">
            <input
              v-model="exportDestination"
              type="radio"
              value="todo"
              name="exportDestination"
            />
            <span>{{ $t('msIntegration.exportToTodo') }}</span>
          </label>
        </div>
      </div>

      <!-- Export Mode Selection -->
      <div v-if="isLoggedIn" class="export-mode-selection">
        <label class="export-mode-label">
          {{ $t('msPlannerIntegration.exportMode') }}:
        </label>
        <div class="export-mode-options">
          <label class="radio-option">
            <input
              v-model="exportMode"
              type="radio"
              value="phase"
              name="exportMode"
            />
            <span>{{ $t('msPlannerIntegration.exportPhase') }}</span>
          </label>
          <label
            class="radio-option"
            :class="{ disabled: !hasSelectedActivities }"
          >
            <input
              v-model="exportMode"
              type="radio"
              value="selected"
              name="exportMode"
              :disabled="!hasSelectedActivities"
            />
            <span>
              {{
                hasSelectedActivities
                  ? $t('msPlannerIntegration.exportSelectedCount', {
                      count: selectedActivityIds.length,
                    })
                  : $t('msPlannerIntegration.exportSelectedNone')
              }}
            </span>
          </label>
        </div>
      </div>

      <!-- Planner-specific fields -->
      <template v-if="isLoggedIn && exportDestination === 'planner'">
        <UiSelect
          v-model="selectedGroup"
          name="group"
          :disabled="groups.length === 0"
          :label="$t('msPlannerIntegration.groupSelection')"
          :options="groups"
        />
        <UiSelect
          v-model="selectedPlanner"
          name="planner"
          :disabled="!groupSelected || planners.length === 0"
          :label="$t('msPlannerIntegration.plannerSelection')"
          :options="planners"
        />
        <UiCheckboxWithLabel
          :value="includeCriteriaAsChecklist"
          @toggle="includeCriteriaAsChecklist = !includeCriteriaAsChecklist"
        >
          <div>
            <div>
              {{ $t('msPlannerIntegration.includeCriteriaAsChecklist') }}
            </div>
            <div class="checkbox-hint">
              {{ $t('msPlannerIntegration.includeCriteriaAsChecklistHint') }}
            </div>
          </div>
        </UiCheckboxWithLabel>
      </template>

      <!-- Todo-specific fields -->
      <template v-if="isLoggedIn && exportDestination === 'todo'">
        <UiSelect
          v-model="selectedTodoList"
          name="todoList"
          :disabled="!isLoggedIn"
          :label="$t('msTodoIntegration.todoListSelection')"
          :options="todoLists"
          :placeholder="
            todoLists.length === 0
              ? $t('msTodoIntegration.noListsFound')
              : undefined
          "
        />
        <div
          v-if="isLoggedIn && todoLists.length === 0"
          class="no-lists-message"
        >
          <p>{{ $t('msTodoIntegration.noListsMessage') }}</p>
        </div>
        <UiCheckboxWithLabel
          v-if="todoLists.length > 0"
          :value="includeCriteriaAsChecklistTodo"
          @toggle="
            includeCriteriaAsChecklistTodo = !includeCriteriaAsChecklistTodo
          "
        >
          <div>
            <div>{{ $t('msTodoIntegration.includeCriteriaAsChecklist') }}</div>
            <div class="checkbox-hint">
              {{ $t('msTodoIntegration.includeCriteriaAsChecklistHint') }}
            </div>
          </div>
        </UiCheckboxWithLabel>
      </template>

      <!-- Phase selection (for both destinations) -->
      <UiSelect
        v-if="isLoggedIn && exportMode === 'phase' && exportDestination"
        v-model="selectedPhase"
        name="phase"
        :disabled="
          (exportDestination === 'planner' && !plannerSelected) ||
          (exportDestination === 'todo' && !todoListSelected)
        "
        :label="$t('msPlannerIntegration.phaseSelection')"
        :options="phases"
      />

      <!-- No planners message -->
      <div
        v-if="
          isLoggedIn && exportDestination === 'planner' && groups.length === 0
        "
        class="no-lists-message"
      >
        <p>{{ $t('msPlannerIntegration.noPlannersFound') }}</p>
      </div>

      <!-- Export button -->
      <UiButton
        :disabled="!canExport || clickedExport"
        :loading="isLoading"
        @click="handleExport"
      >
        {{ $t('msPlannerIntegration.createExport') }}
      </UiButton>
    </div>
  </UiModal>
</template>

<style lang="scss" scoped>
.buttons {
  display: flex;
  justify-content: flex-end;
  gap: 5px;
  margin-top: 5px;
}
.main {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: var(--space-12);
}

.export-destination-selection,
.export-mode-selection {
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
  padding: var(--space-12);

  .export-destination-label,
  .export-mode-label {
    font-weight: var(--font-weight-bold);
    color: var(--color-gray-900);
  }

  .export-destination-options,
  .export-mode-options {
    display: flex;
    flex-direction: column;
    gap: var(--space-8);

    .radio-option {
      display: flex;
      align-items: center;
      gap: var(--space-8);
      cursor: pointer;

      &.disabled {
        opacity: 0.5;
        cursor: not-allowed;

        input {
          cursor: not-allowed;
        }
      }

      input[type='radio'] {
        cursor: pointer;
      }

      span {
        font-size: var(--font-size-14);
      }
    }
  }
}

.checkbox-hint {
  font-size: var(--font-size-12);
  color: var(--color-gray-600);
  margin-top: var(--space-4);
}

.selected-activities-info {
  padding: var(--space-12);
  background-color: var(--color-bluelight-50);
  border-radius: var(--border-radius-4);

  p {
    margin: 0;
    color: var(--color-bluelight-700);
    font-size: var(--font-size-14);
  }
}

.no-lists-message {
  padding: var(--space-12);
  background-color: var(--color-warning-50);
  border-radius: var(--border-radius-4);

  p {
    margin: 0;
    color: var(--color-warning-700);
    font-size: var(--font-size-14);
  }
}
</style>
